/**
 * Shared dependencies
 */
import type { RecipeListItem } from '../../shared/types/lists';

/**
 * Internal dependencies
 */
export type AddItemsToUserListItem = Pick<RecipeListItem, 'matterId' | 'matterType' | 'matterName' | 'parentRecipeIds'>;
export type AddItemsToUserListParams = {
	listId: string;
	items?: AddItemsToUserListItem[];
};

export type AddSingleItemToUserListParams = {
	listId: string;
	item: AddItemsToUserListItem;
};

/**
 * Adds items to an existing user list in the Valio API.
 * @link https://evermade.stoplight.io/docs/valio-app/tp5aoq5g9g1kh-post-users-user-id-lists-list-id-items
 * @param {AddItemsToUserListParams} params - The parameters for adding items to the list.
 * @param {string} params.listId - The ID of the list to add items to.
 * @param {Array.<{matterId: string, matterType: string, parentRecipeIds?: string[]}>} [params.items] - The items to add to the list.
 * @param {string} params.items[].matterId - The ID of the matter (item) to add.
 * @param {string} params.items[].matterType - The type of the matter (item) to add.
 * @param {string} params.items[].matterName - The name of the matter (item) to add.
 * @param {string[]} [params.items[].parentRecipeIds] - Optional array of parent recipe IDs associated with the matter.
 * @return {Promise<RecipeListItem[]>} A promise that resolves to the updated user's list items.
 */
export async function addItemsToUserList(
	params: AddItemsToUserListParams
): Promise<RecipeListItem[]> {
	const { host } = window.valioConfig;
	const { session } = window.valioApp;

	// Validate required params
	if (!params.items || params.items.length === 0) {
		throw new Error('Items are required to add to a user list.');
	}

	if (!params.listId) {
		throw new Error('List ID is required to add items to a user list.');
	}

	// Ensure the session is initialized
	if (!session.initialized) {
		await session.init();
	}

	// Ensure the user is authenticated
	if (!session.userId) {
		return Promise.reject(new Error('User is not authenticated. Cannot add items to list.'));
	}

	const headers = await session.authHeader();

	// Make the API request to create the list
	return fetch(`${host}/api/v1/users/${session.userId}/lists/${params.listId}/items`, {
		method: 'POST',
		headers,
		body: JSON.stringify(params.items),
	})
		.then(async (response) => {
			if (!response.ok) {
				// Handle HTTP errors like 401, 404, 500, etc.
				const errorText = await response.text();
				throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
			}

			return await response.json();
		})
		.catch((error) => {
			console.error('Error creating user list:', error);
			throw error;
		});
}
